import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';
import Header from '../../components/Header';
import ResponsiveTableRegion from '../../components/ResponsiveTableRegion';
import LabeledSelectionCheckbox from '../../components/LabeledSelectionCheckbox';
import { ventrisConfig } from '../../ventris.config';

import GroupPageIcon from '../../icons/GroupPageIcon';
import { getUseCaseTerminology } from '../../useCase';
import { completedTestDestination, userHasResultsAccess } from '../../assessmentAccess';

type ClinicianClassProps = {
};

function ClinicianClass({}: ClinicianClassProps) {
  const terms = getUseCaseTerminology();
  const routeParams = useParams();
  const [classInfo, setClassInfo] = useState({} as any);
  const [students, setStudents] = useState([] as any);
  const [tests, setTests] = useState([] as any);
  const [selectedTestIds, setSelectedTestIds] = useState([] as any);
  const [studentPage, setStudentPage] = useState(1);
  const [studentSizePerPage, setStudentSizePerPage] = useState(20);
  const [studentTotalSize, setStudentTotalSize] = useState(0);
  const [studentSortFieldName, setStudentSortFieldName] = useState("");
  const [studentSortDirection, setStudentSortDirection] = useState("");
  const [studentSearch, setStudentSearch] = useState("");
  const [debouncedStudentSearch, setDebouncedStudentSearch] = useState("");
  const [testPage, setTestPage] = useState(1);
  const [testSizePerPage, setTestSizePerPage] = useState(20);
  const [testTotalSize, setTestTotalSize] = useState(0);
  const [testSortFieldName, setTestSortFieldName] = useState("");
  const [testSortDirection, setTestSortDirection] = useState("");
  const [testSearch, setTestSearch] = useState("");
  const [debouncedTestSearch, setDebouncedTestSearch] = useState("");
  const [completionStatusFilter, setCompletionStatusFilter] = useState("");
  const [archivedFilter, setArchivedFilter] = useState("");
  const [testTab, setTestTab] = useState("view-all");

  useEffect(() => {
    loadClass();
  }, [routeParams.classId]);

  useEffect(() => {
    loadStudents(studentPage);
  }, [studentPage, studentSizePerPage, studentSortFieldName, studentSortDirection, debouncedStudentSearch, routeParams.classId]);

  useEffect(() => {
    loadTests(testPage);
  }, [testPage, testSizePerPage, testSortFieldName, testSortDirection, debouncedTestSearch, completionStatusFilter, archivedFilter, routeParams.classId]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedStudentSearch(studentSearch);
      if (studentPage !== 1) {
        setStudentPage(1);
      }
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [studentSearch]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedTestSearch(testSearch);
      if (testPage !== 1) {
        setTestPage(1);
      }
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [testSearch]);

  const loadClass = () => {
    const token = sessionStorage.getItem("token");
    fetch(ventrisConfig.url + '/api/class/' + routeParams.classId, {
      method: 'get',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      }
    }).then(function (response) {
      return response.json();
    }).then(async function (data) {
      if (data.class) {
        setClassInfo(data.class);
      }
    });
  }

  const loadStudents = (pageNumber: number) => {
    const token = sessionStorage.getItem("token");
    fetch(ventrisConfig.url + '/api/students?page=' + pageNumber + '&sizePerPage=' + studentSizePerPage + '&sortField=' + studentSortFieldName + '&sortDirection=' + studentSortDirection + '&organization=' + routeParams.classId + '&archived=false&search=' + encodeURIComponent(debouncedStudentSearch), {
      method: 'get',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      }
    }).then(function (response) {
      return response.json();
    }).then(async function (data) {
      setStudents(data.students || []);
      setStudentTotalSize(data.total_records || 0);
    });
  }

  const loadTests = (pageNumber: number) => {
    const token = sessionStorage.getItem("token");
    fetch(ventrisConfig.url + '/api/tests?page=' + pageNumber + '&sizePerPage=' + testSizePerPage + '&sortField=' + testSortFieldName + '&sortDirection=' + testSortDirection + '&organization=' + routeParams.classId + '&completionStatus=' + completionStatusFilter + '&archived=' + archivedFilter + '&search=' + encodeURIComponent(debouncedTestSearch), {
      method: 'get',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      }
    }).then(function (response) {
      return response.json();
    }).then(async function (data) {
      setTests(data.tests || []);
      setTestTotalSize(data.total_records || 0);
      setSelectedTestIds([]);
    });
  }

  const back = () => {
    document.location.href = "/classes";
  }

  const loadStudent = (studentId: any) => {
    document.location.href = "/student/" + studentId;
  }

  const loadTest = (name: any, testId: any) => {
    document.location.href = "/test/" + name + "/" + testId;
  }

  const toggleSelectedTest = (testId: string | number) => {
    const normalizedId = String(testId);
    setSelectedTestIds((prev: any) =>
      prev.includes(normalizedId)
        ? prev.filter((item: any) => item !== normalizedId)
        : [...prev, normalizedId]
    );
  }

  const archiveSelectedTests = (archived: boolean) => {
    if (selectedTestIds.length === 0) {
      return;
    }

    const token = sessionStorage.getItem("token");
    fetch(ventrisConfig.url + '/api/test/' + (archived ? 'unarchive' : 'archive'), {
      method: 'post',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      },
      body: JSON.stringify({
        tests: selectedTestIds.join("|")
      })
    }).then(function (response) {
      return response.json();
    }).then(async function () {
      loadTests(1);
      setTestPage(1);
    });
  }

  const handleTestTabChange = (tabKey: string | null) => {
    const nextTab = tabKey || "view-all";
    setTestTab(nextTab);

    if (nextTab === "view-all") {
      setCompletionStatusFilter("");
      setArchivedFilter("");
    } else if (nextTab === "completed") {
      setCompletionStatusFilter("Complete");
      setArchivedFilter("");
    } else if (nextTab === "in-progress") {
      setCompletionStatusFilter("Incomplete");
      setArchivedFilter("");
    } else if (nextTab === "archived") {
      setCompletionStatusFilter("");
      setArchivedFilter("true");
    }

    setTestPage(1);
  }

  const deleteSelectedTests = () => {
    if (selectedTestIds.length === 0) {
      return;
    }

    const token = sessionStorage.getItem("token");
    fetch(ventrisConfig.url + '/api/test/delete', {
      method: 'post',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      },
      body: JSON.stringify({
        tests: selectedTestIds.join("|")
      })
    }).then(function (response) {
      return response.json();
    }).then(async function () {
      loadTests(1);
      setTestPage(1);
    });
  }

  const unarchiveTest = (testId: string | number) => {
    const token = sessionStorage.getItem("token");
    fetch(ventrisConfig.url + '/api/test/unarchive', {
      method: 'post',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      },
      body: JSON.stringify({
        tests: String(testId)
      })
    }).then(function (response) {
      return response.json();
    }).then(async function () {
      loadTests(testPage);
    });
  }

  const studentColumns = [
    {
      dataField: 'last_name',
      text: 'Last Name',
      sort: true,
      formatter: (cell: any, row: any) => <Button variant="link" size="sm" className="classes-inline-link" onClick={() => loadStudent(row.id)}>{cell}</Button>
    },
    {
      dataField: 'first_name',
      text: 'First Name',
      sort: true
    },
    {
      dataField: 'grade',
      text: 'Grade',
      sort: true
    },
    {
      dataField: 'birthdate',
      text: 'Age',
      formatter: (cell: any) => {
        if (!cell) {
          return 'N/A';
        }
        const birthdate = new Date(cell);
        const today = new Date();
        let age = today.getFullYear() - birthdate.getFullYear();
        const monthDelta = today.getMonth() - birthdate.getMonth();
        if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < birthdate.getDate())) {
          age--;
        }
        return Number.isNaN(age) ? 'N/A' : age;
      }
    },
    {
      dataField: 'school',
      text: 'School',
      formatter: () => classInfo.school_name || 'N/A'
    },
    {
      dataField: 'class_name',
      text: terms.class,
      formatter: () => classInfo.name || 'N/A'
    },
    {
      dataField: 'id',
      text: 'Action',
      formatter: (cell: any, row: any) => <Button variant="link" size="sm" className="classes-inline-link" onClick={() => loadStudent(row.id)}>View</Button>
    }
  ];

  const testColumns = [
    {
      dataField: 'id',
      text: 'Select',
      headerFormatter: () => <span className="visually-hidden">Select tests</span>,
      formatter: (cell: any, row: any) => <LabeledSelectionCheckbox
        id={`class-test-select-${row.id}`}
        label={`Select ${row.name} test for ${row.student_first_name} ${row.student_last_name}`}
        checked={selectedTestIds.includes(String(row.id))}
        onChange={() => toggleSelectedTest(row.id)}
      />
    },
    {
      dataField: 'name',
      text: 'Test Name',
      sort: true,
      formatter: (cell: any, row: any) => (
        <div className="classes-test-name-cell">
          <span aria-hidden="true" className={row.archived ? "ventris-table-status-archived" : (row.status === "Complete" ? "ventris-table-status-completed" : "ventris-table-status-in-progress")}></span>
          <span className="visually-hidden">{row.archived ? "Archived: " : `${row.status}: `}</span>
          <Button variant="link" size="sm" className="classes-inline-link" onClick={() => row.status === "Complete" ? document.location.href = completedTestDestination(row) : loadTest(row.name, row.id)}>
            {cell}
          </Button>
        </div>
      )
    },
    {
      dataField: 'student_name',
      text: `${terms.student} Name`,
      formatter: (cell: any, row: any) => (
        <Button variant="link" size="sm" className="classes-inline-link" onClick={() => loadStudent(row.student_id)}>
          {row.student_first_name + " " + row.student_last_name}
        </Button>
      )
    },
    {
      dataField: 'created_at',
      text: 'Date Administered',
      sort: true,
      formatter: (cell: any) => (new Date(Date.parse(cell))).toLocaleDateString()
    },
    {
      dataField: 'clinician',
      text: 'Examiner',
      sort: true
    },
    {
      dataField: 'mode',
      text: 'Mode'
    },
    {
      dataField: 'id',
      text: 'Action',
      formatter: (cell: any, row: any) => {
        if (row.status === "Complete") {
          return <Button variant="link" size="sm" className="classes-inline-link" onClick={() => document.location.href = completedTestDestination(row)}>Show Results</Button>;
        }

        if (row.archived) {
          return <Button variant="link" size="sm" className="classes-inline-link" onClick={() => unarchiveTest(row.id)}>Unarchive</Button>;
        }

        return <Button variant="link" size="sm" className="classes-inline-link" onClick={() => loadTest(row.name, row.id)}>Continue Test</Button>;
      }
    }
  ];

  return (
    <Container fluid className="ventris-content-wrapper ventris-classes">
      <Header portal={sessionStorage.getItem("portal")!} page="classes" />
      <Container as="main" id="main-content" tabIndex={-1}>
        <Row>
          <Col>
            <div className="classes-page-shell">
              <div className="classes-toolbar classes-toolbar-detail">
                <h1 className="page-header page-header-no-margin align-items-center">
                  <GroupPageIcon />
                  <div className="flex-grow">{terms.classes}</div>
                </h1>
                <div className="classes-toolbar-actions">
                  {userHasResultsAccess(JSON.parse(sessionStorage.getItem("current_user") || "{}")) ? <Button variant="primary" className="ventris-button-primary classes-results-button" onClick={() => document.location.href = "/tests"}>
                    View Results
                  </Button> : null}
                </div>
              </div>

              <Button variant="link" size="sm" className="back-link" onClick={back}>Back to {terms.classes}</Button>

              <section className="classes-section">
                <h3 className="classes-section-title">{terms.class} Info</h3>
                <ResponsiveTableRegion label={`${terms.class} information`} className="ventris-reports">
                  <div className="react-bootstrap-table react-bootstrap-table-rounded">
                    <table className="table table-bordered">
                      <thead>
                        <tr>
                          <th>{terms.class} Name</th>
                          <th>School</th>
                          <th>{terms.teacher}</th>
                          <th>Number of {terms.students}</th>
                          <th>Number of Tests</th>
                          <th>Grade</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{classInfo.name || "N/A"}</td>
                          <td>{classInfo.school_name || "N/A"}</td>
                          <td>{classInfo.teacher_names || "Unassigned"}</td>
                          <td>{classInfo.student_count ?? 0}</td>
                          <td>{classInfo.test_count ?? 0}</td>
                          <td>{classInfo.grade || "N/A"}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </ResponsiveTableRegion>
              </section>

              <section className="classes-section">
                <h3 className="classes-section-title">{terms.class} {terms.students}</h3>
                <div className="classes-section-toolbar">
                  <Form.Control type="search" className="ventris-search classes-search" aria-label={`Search ${terms.students.toLowerCase()}`} placeholder={`Search ${terms.students}`} value={studentSearch} onChange={(event) => setStudentSearch(event.target.value)} />
                </div>
                <ResponsiveTableRegion label={`${terms.class} ${terms.students}`} className="ventris-students classes-students-table">
                  <BootstrapTable
                    keyField='id'
                    data={students}
                    columns={studentColumns}
                    bordered={true}
                    remote={{pagination: true, sort: true}}
                    pagination={paginationFactory({ page: studentPage, sizePerPage: studentSizePerPage, totalSize: studentTotalSize })}
                    onTableChange={(type: string, { page, sizePerPage, sortField, sortOrder }: any) => {
                      if ((sortField && studentSortFieldName !== sortField) || (studentSortDirection && studentSortDirection !== sortOrder)) {
                        setStudentPage(1);
                      } else {
                        setStudentPage(page);
                      }
                      setStudentSizePerPage(sizePerPage);
                      setStudentSortFieldName(sortField ? sortField : "");
                      setStudentSortDirection(sortOrder ? sortOrder : "");
                    }}
                  />
                </ResponsiveTableRegion>
              </section>

              <section className="classes-section">
                <div className="classes-tests-header">
                  <h3 className="classes-section-title">{terms.class} Tests</h3>
                  <div className="ventris-action-links">
                    <Button variant="link" size="sm" className="ventris-action-link" onClick={() => archiveSelectedTests(false)}>Archive Selected</Button>
                    <Button variant="link" size="sm" className="ventris-action-link" onClick={deleteSelectedTests}>Delete Selected</Button>
                  </div>
                </div>
                <div className="classes-section-toolbar">
                  <Form.Control type="search" className="ventris-search classes-search" aria-label="Search tests" placeholder="Search Tests" value={testSearch} onChange={(event) => setTestSearch(event.target.value)} />
                </div>
                <ResponsiveTableRegion label={`${terms.class} tests`} className="ventris-tests classes-tests-table">
                  <Tabs activeKey={testTab} onSelect={handleTestTabChange}>
                    <Tab eventKey="view-all" title="View All">
                    </Tab>
                    <Tab eventKey="completed" title="Completed">
                    </Tab>
                    <Tab eventKey="in-progress" title="In Progress">
                    </Tab>
                    <Tab eventKey="archived" title="Archived">
                    </Tab>
                  </Tabs>
                  <BootstrapTable
                    keyField='id'
                    data={tests}
                    columns={testColumns}
                    bordered={true}
                    remote={{pagination: true, sort: true}}
                    pagination={paginationFactory({ page: testPage, sizePerPage: testSizePerPage, totalSize: testTotalSize })}
                    onTableChange={(type: string, { page, sizePerPage, sortField, sortOrder }: any) => {
                      if ((sortField && testSortFieldName !== sortField) || (testSortDirection && testSortDirection !== sortOrder)) {
                        setTestPage(1);
                      } else {
                        setTestPage(page);
                      }
                      setTestSizePerPage(sizePerPage);
                      setTestSortFieldName(sortField ? sortField : "");
                      setTestSortDirection(sortOrder ? sortOrder : "");
                    }}
                  />
                </ResponsiveTableRegion>
              </section>
            </div>
          </Col>
        </Row>
      </Container>
    </Container>
  );
}

export default ClinicianClass;
